import React from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import {
  jsonPointerGet,
  parseToolCallParamBindingRef,
} from '@cotera/utilities';
import { Text } from '@cotera/client/app/components/ui';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import { PayloadBlock, Section, StepHeader } from './run-step-view-sections';
import type { WorkflowRunStepView, WorkflowRunStepViewProps } from './types';

const {
  WorkflowNodeType,
  FILTER_OPERATOR_LABELS,
  FILTER_OPERATORS_REQUIRING_LIST,
  parseFilterConfig,
  parseListValue,
  isNodeScopedFilterPath,
} = PersistedWorkflow;

/** Escapes a JSON Pointer reference token (`~` -> `~0`, `/` -> `~1`). */
function escapePointerToken(token: string): string {
  return token.replace(/~/g, '~0').replace(/\//g, '~1');
}

/**
 * Human field label for a condition path (bare pointer or `Node/pointer` ref).
 * A node-scoped ref renders its source with the friendly label (the agent's
 * title, same as the `/` picker) when available, else the raw source name.
 */
export function describeField(
  path: string,
  sourceLabels?: ReadonlyMap<string, string>
): string {
  if (path === '') {
    return 'the whole value';
  }
  if (!isNodeScopedFilterPath(path)) {
    return path;
  }
  try {
    const { sourceName, pointer } = parseToolCallParamBindingRef(path);
    if (sourceName === undefined) {
      return path;
    }
    const source = sourceLabels?.get(sourceName) ?? sourceName;
    const field = pointer === '' ? 'the whole output' : pointer;
    return `${field} from ${source}`;
  } catch {
    return path;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * How a filter condition's checked value resolved for display:
 * - `value`   — resolved from this step's recorded input (bare pointer, or a
 *               fan-in branch keyed by display name).
 * - `absent`  — a direct-input ref that genuinely wasn't present.
 * - `earlier-step` — a `Node/pointer` ref to a node that isn't part of this
 *               step's direct input. It WAS resolved at run time against that
 *               earlier step's output, but that output isn't in this step's
 *               payload, so we can't echo the value here (open that step to see
 *               it). Distinguished from `absent` so a passing filter never reads
 *               as "not present".
 */
export type CheckedResolution =
  | { readonly kind: 'value'; readonly value: unknown }
  | { readonly kind: 'absent' }
  | { readonly kind: 'earlier-step'; readonly sourceName: string };

export function resolveCheckedValue(
  source: unknown,
  path: string
): CheckedResolution {
  try {
    if (!isNodeScopedFilterPath(path)) {
      const value = jsonPointerGet(source, path);
      return value === undefined
        ? { kind: 'absent' }
        : { kind: 'value', value };
    }
    const { sourceName, pointer } = parseToolCallParamBindingRef(path);
    if (sourceName === undefined) {
      return { kind: 'absent' };
    }
    // Fan-in input is a record keyed by each predecessor's display name.
    if (isRecord(source) && sourceName in source) {
      const value = jsonPointerGet(
        source,
        `/${escapePointerToken(sourceName)}${pointer}`
      );
      return value === undefined
        ? { kind: 'absent' }
        : { kind: 'value', value };
    }
    // The referenced node isn't a direct predecessor of this filter — its output
    // lives on that earlier step, not in this step's input.
    return { kind: 'earlier-step', sourceName };
  } catch {
    return { kind: 'absent' };
  }
}

/**
 * The field + operator half of a condition, e.g. `/status is` or `/email exists`.
 * The compared value is rendered separately — inline for scalar operators, as a
 * wrapped chip list for `one of` / `not one of` so a long list stays readable.
 */
function describeConditionPrefix(
  condition: PersistedWorkflow.FilterCondition,
  sourceLabels?: ReadonlyMap<string, string>
): string {
  const field = describeField(condition.path, sourceLabels);
  const operator = FILTER_OPERATOR_LABELS[condition.operator].toLowerCase();
  return `${field} ${operator}`;
}

/** One condition row: the field/operator line, then its value(s). */
function ConditionLine({
  condition,
  lead,
  sourceLabels,
}: {
  condition: PersistedWorkflow.FilterCondition;
  /** Prefix joining this row to the previous one (`Where`, `And`, `Or`), if any. */
  lead?: string;
  sourceLabels?: ReadonlyMap<string, string>;
}): React.ReactElement {
  const prefix = describeConditionPrefix(condition, sourceLabels);
  const isList = FILTER_OPERATORS_REQUIRING_LIST.has(condition.operator);
  const listValues =
    isList && condition.value !== undefined
      ? parseListValue(condition.value)
      : [];
  return (
    <>
      <Text.P className="text-foreground font-medium">
        {lead !== undefined ? `${lead} ` : ''}
        {prefix}
        {!isList && condition.value !== undefined ? (
          <>
            {' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal">
              {condition.value}
            </code>
          </>
        ) : null}
        {isList ? (
          <span className="text-muted-foreground font-normal">
            {' '}
            ({listValues.length}
            {listValues.length === 1 ? ' value' : ' values'})
          </span>
        ) : null}
      </Text.P>
      {isList && listValues.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {listValues.map((value, i) => (
            <code
              key={i}
              className="rounded bg-muted px-1.5 py-0.5 text-xs break-words"
            >
              {value}
            </code>
          ))}
        </div>
      ) : null}
    </>
  );
}

/** How the gate resolved, derived from the recorded step status. */
function outcome(status: string | null): {
  headline: string;
  detail: string;
  className: string;
} {
  switch (status) {
    case 'completed':
      return {
        headline: 'Condition met — execution continued',
        detail: 'The downstream steps ran.',
        className: 'text-emerald-700',
      };
    case 'stopped':
      return {
        headline: 'Condition not met — execution stopped here',
        detail: 'No downstream steps ran for this run.',
        className: 'text-muted-foreground',
      };
    case 'failed':
      return {
        headline: 'Filter could not run',
        detail: 'The condition was not evaluated — see the error below.',
        className: 'text-destructive',
      };
    case 'cancelled':
      return {
        headline: 'Run cancelled',
        detail: 'This filter was cancelled before it finished.',
        className: 'text-muted-foreground',
      };
    default:
      // queued / incomplete / no step row yet
      return {
        headline: 'Not yet evaluated',
        detail: 'This filter has not finished running.',
        className: 'text-muted-foreground',
      };
  }
}

/** Renders the value the filter actually checked, or a "(not present)" marker. */
function CheckedValue({
  source,
  path,
  sourceLabels,
}: {
  source: unknown;
  path: string;
  sourceLabels?: ReadonlyMap<string, string>;
}): React.ReactElement {
  const resolution = resolveCheckedValue(source, path);
  const label =
    path === ''
      ? 'Checked value'
      : `Value at ${describeField(path, sourceLabels)}`;
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <Text.Caption className="text-muted-foreground">{label}:</Text.Caption>
      {resolution.kind === 'value' ? (
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
          {JSON.stringify(resolution.value)}
        </code>
      ) : resolution.kind === 'earlier-step' ? (
        <Text.Caption className="italic text-muted-foreground">
          resolved from that earlier step's output (open it to see the value)
        </Text.Caption>
      ) : (
        <Text.Caption className="italic text-muted-foreground">
          not present
        </Text.Caption>
      )}
    </div>
  );
}

/**
 * Run-step view for a filter node: explains the configured condition and how it
 * resolved (met → continued, not met → stopped) so a stopped run reads clearly
 * rather than as an opaque empty step.
 */
const FilterRunStepView: React.FC<WorkflowRunStepViewProps> = ({
  node,
  payload,
  sourceLabels,
}) => {
  const parsed = parseFilterConfig(node.data);
  const result = outcome(node.status);
  // The filter passes its input through, so input/output hold the same value;
  // fall back to output when input wasn't recorded on older runs.
  const checkedSource = payload?.input ?? payload?.output;

  return (
    <div className="h-full overflow-y-auto bg-background px-6 py-6 flex flex-col gap-6">
      <StepHeader name={node.name} status={node.status} />

      <Section
        title={
          parsed.ok && parsed.config.groups.length > 1
            ? parsed.config.combinator === 'and'
              ? 'Conditions (every group must match)'
              : 'Conditions (any group must match)'
            : parsed.ok && (parsed.config.groups[0]?.conditions.length ?? 0) > 1
            ? 'Conditions'
            : 'Condition'
        }
      >
        {parsed.ok ? (
          <div className="space-y-3 rounded-lg border p-4">
            {parsed.config.groups.map((group, groupIndex) => {
              const multiGroup = parsed.config.groups.length > 1;
              const multiCondition = group.conditions.length > 1;
              const groupJoin =
                parsed.config.combinator === 'and' ? 'AND' : 'OR';
              const rowJoin = group.combinator === 'and' ? 'And' : 'Or';
              return (
                <div key={groupIndex} className="space-y-2">
                  {multiGroup && groupIndex > 0 ? (
                    <Text.Caption className="block font-medium text-muted-foreground">
                      {groupJoin}
                    </Text.Caption>
                  ) : null}
                  {group.conditions.map((condition, index) => (
                    <div key={index} className="space-y-1">
                      <ConditionLine
                        condition={condition}
                        lead={
                          multiCondition
                            ? index === 0
                              ? 'Where'
                              : rowJoin
                            : undefined
                        }
                        sourceLabels={sourceLabels}
                      />
                      <CheckedValue
                        source={checkedSource}
                        path={condition.path}
                        sourceLabels={sourceLabels}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            <Text.P className={`text-sm font-medium ${result.className}`}>
              {result.headline}
            </Text.P>
            <Text.Caption className="block text-muted-foreground">
              {result.detail}
            </Text.Caption>
          </div>
        ) : (
          <pre className="rounded-lg border border-destructive/30 bg-destructive/5 text-destructive/90 text-xs p-4 overflow-auto whitespace-pre-wrap">
            {parsed.error}
          </pre>
        )}
      </Section>

      {node.error !== null && node.error !== '' ? (
        <Section title="Error">
          <pre className="rounded-lg border border-destructive/30 bg-destructive/5 text-destructive/90 text-xs p-4 overflow-auto whitespace-pre-wrap">
            {node.error}
          </pre>
        </Section>
      ) : null}

      <Separator />

      <Section title="Input">
        <PayloadBlock
          value={payload?.input ?? null}
          emptyText="No input recorded for this step."
        />
      </Section>

      <Separator />

      <Section title="Output">
        <PayloadBlock
          value={payload?.output ?? null}
          emptyText="No output recorded for this step."
        />
      </Section>
    </div>
  );
};

export const filterRunStepView: WorkflowRunStepView = {
  key: 'filter',
  matches: (nodeType) => nodeType === WorkflowNodeType.Filter,
  needsPayload: true,
  render: (props) => <FilterRunStepView {...props} />,
};
